import {
  Badge,
  Box,
  Card,
  Flex,
  Grid,
  Group,
  Stack,
  Text,
  Title,
  rem,
  Divider,
  Table,
} from "@mantine/core";
import { IconAlertCircle, IconCheck, IconInfoCircle } from "@tabler/icons-react";
import React from "react";
import { ReadingRiskResult, RiskMetric } from "../utils/ReadingRiskUtils";
import { ReadingRiskGauge } from "./ReadingRiskGauge";
import { ThresholdGauge } from "./ThresholdGauge";
import { BellCurvePercentile } from "./BellCurvePercentile";
import { getReadingInsightSuggestions } from "../utils/ReadingInsightRubric";
import "./ReadingAssessmentDashboard.scss";

interface Props {
  risk: ReadingRiskResult;
}

const statusToMantineColor = (status: "green" | "amber" | "red"): string =>
  status === "amber" ? "yellow" : status;

const statusToLabel = (status: "green" | "amber" | "red"): string => {
  if (status === "red") return "Attention";
  if (status === "amber") return "Monitor";
  return "Normal";
};

const ReadingRiskOverviewHeader: React.FC<{ risk: ReadingRiskResult }> = ({ risk }) => {
  const getIcon = () => {
    if (risk.isInconclusive) return <IconAlertCircle size={32} />;
    if (risk.riskColor === "green") return <IconCheck size={32} />;
    return <IconInfoCircle size={32} />;
  };

  return (
    <Card
      withBorder
      radius="md"
      p="md"
      h="100%"
      style={{
        backgroundColor: `var(--mantine-color-${risk.riskColor}-0)`,
        borderColor: `var(--mantine-color-${risk.riskColor}-3)`,
        borderLeft: `${rem(8)} solid var(--mantine-color-${risk.riskColor}-6)`,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Stack gap="sm">
        <Flex align="center" gap="sm">
          <Box c={`${risk.riskColor}.6`}>{getIcon()}</Box>
          <Box>
            <Text size="xs" fw={800} tt="uppercase" c={`${risk.riskColor}.7`} lts={2}>
              Reading Assessment Result
            </Text>
            <Title order={2} c={`${risk.riskColor}.9`} style={{ fontSize: rem(24) }}>
              {risk.riskLabel}
            </Title>
          </Box>
        </Flex>

        <Divider color={`${risk.riskColor}.2`} />

        <Box>
          <Text size="sm" fw={500} c={`${risk.riskColor}.9`} style={{ lineHeight: 1.5 }}>
            {risk.isInconclusive
              ? "Inconclusive — the comprehension check suggests the passage may not have been read attentively. We recommend repeating the assessment."
              : risk.totalScore >= 7
                ? "The eye movement patterns observed during this test suggest a higher likelihood of reading-related challenges. We recommend reviewing the specific risk factors below with a specialist."
                : risk.totalScore >= 5
                  ? "The test suggests monitoring reading ability. While some metrics are within normal ranges, others show slight deviations that may affect reading fluency over time."
                  : "The test demonstrates reading ability within the expected range. Eye movements were efficient, and reading speed is consistent with normative data for this age group."}
          </Text>
        </Box>

        <Group gap="xs">
          {!risk.isInconclusive && risk.confidence !== "normal" && (
            <Badge size="sm" color="yellow" radius="sm">
              Lower Confidence — Repeat Recommended
            </Badge>
          )}
          <Badge size="sm" color="gray" radius="sm">
            AGE BAND: {risk.ageBand}
          </Badge>
        </Group>
      </Stack>
    </Card>
  );
};

const ReadingInsightCard: React.FC<{ risk: ReadingRiskResult }> = ({ risk }) => {
  const suggestions = getReadingInsightSuggestions(risk);

  return (
    <Card withBorder radius="md" p="lg" h="100%">
      <Text size="xs" fw={800} c="dimmed" tt="uppercase" lts={1} mb="md">
        Actionable Insights
      </Text>
      <Stack gap="md">
        {suggestions.map((suggestion) => (
          <Box key={suggestion.title}>
            <Text fw={800} size="sm" c="dark.7">
              {suggestion.title}
            </Text>
            <Text size="sm" c="gray.7" style={{ lineHeight: 1.5 }}>
              {suggestion.description}
            </Text>
          </Box>
        ))}
      </Stack>
    </Card>
  );
};

const METRIC_HEADER_MIN_HEIGHT = rem(36);
const METRIC_VALUE_LINE_HEIGHT = 1.05;
const METRIC_VALUE_MIN_HEIGHT = rem(60);
const METRIC_REFERENCE_MIN_HEIGHT = rem(18);

const ReadingRiskMetricCard: React.FC<{ metric: RiskMetric }> = ({ metric }) => {
  return (
    <Card
      withBorder
      radius="md"
      p="lg"
      shadow="sm"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
      }}
    >
      <Stack gap="xs" style={{ flex: 1 }}>
        <Group
          justify="space-between"
          wrap="nowrap"
          align="flex-start"
          mih={METRIC_HEADER_MIN_HEIGHT}
        >
          <Text fw={800} size="xs" c="dimmed" tt="uppercase" lts={1}>
            {metric.label}
          </Text>
          <Group gap={4}>
            <Badge color={statusToMantineColor(metric.status)} size="sm" radius="sm">
              {statusToLabel(metric.status)}
            </Badge>
            <Badge size="xs" color="gray" radius="xs">
              +{metric.points} PT
            </Badge>
          </Group>
        </Group>

        <Box my="sm">
          <Text
            size={rem(28)}
            fw={900}
            c={metric.status === "green" ? "dark.8" : `${statusToMantineColor(metric.status)}.8`}
            style={{
              lineHeight: METRIC_VALUE_LINE_HEIGHT,
              minHeight: METRIC_VALUE_MIN_HEIGHT,
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            {metric.value}
          </Text>
          <Text size="xs" c="dimmed" fw={600} mih={METRIC_REFERENCE_MIN_HEIGHT}>
            {metric.normReference}
          </Text>
        </Box>

        <Box my="xs">
          <ThresholdGauge zones={metric.gauge.zones} rawValue={metric.gauge.rawValue} />
        </Box>

        <Box my="xs">
          <BellCurvePercentile
            data={metric.percentile}
            statusColor={metric.status}
            label="Performance"
          />
        </Box>

        <Divider variant="dashed" my="xs" />

        <Text size="sm" c="gray.8" style={{ lineHeight: 1.5, flex: 1 }}>
          {metric.rationale}
        </Text>
      </Stack>
    </Card>
  );
};

const NumericSummaryTable: React.FC<{ risk: ReadingRiskResult }> = ({ risk }) => {
  const formatPercent = (value: number) => `${Math.round(value)}%`;
  const ordinalSuffix = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  const metricRows = [
    {
      name: "Reading Speed",
      measured: risk.metrics.speed.value,
      expected: risk.metrics.speed.expectedRange,
      percentile: ordinalSuffix(risk.metrics.speed.percentile.percentile),
      status: risk.metrics.speed.status,
      points: `${risk.metrics.speed.points} / 2`,
    },
    {
      name: "Fixation Frequency",
      measured: risk.metrics.fixations.value,
      expected: risk.metrics.fixations.expectedRange,
      percentile: ordinalSuffix(risk.metrics.fixations.percentile.percentile),
      status: risk.metrics.fixations.status,
      points: `${risk.metrics.fixations.points} / 2`,
    },
    {
      name: "Regression Rate",
      measured: risk.metrics.regressions.value,
      expected: risk.metrics.regressions.expectedRange,
      percentile: ordinalSuffix(risk.metrics.regressions.percentile.percentile),
      status: risk.metrics.regressions.status,
      points: `${risk.metrics.regressions.points} / 2`,
    },
    {
      name: "Fixation Duration",
      measured: risk.metrics.duration.value,
      expected: risk.metrics.duration.expectedRange,
      percentile: ordinalSuffix(risk.metrics.duration.percentile.percentile),
      status: risk.metrics.duration.status,
      points: `${risk.metrics.duration.points} / 1`,
    },
  ];
  const fixationRows = [
    {
      name: "Total Fixations",
      measured: risk.fixationSummary.totalFixations.toLocaleString(),
      expected: "N.A.",
    },
    {
      name: "Normal Fixations",
      measured: risk.fixationSummary.normalFixationsCount.toLocaleString(),
      expected: "N.A.",
    },
    {
      name: "Normal Fixations Percentage",
      measured: formatPercent(risk.fixationSummary.normalFixationsPercentage),
      expected: "Higher is better; average val. N.A.",
    },
    {
      name: "Excessive Fixations",
      measured: risk.fixationSummary.excessiveFixationsCount.toLocaleString(),
      expected: "Lower is better; average val. N.A.",
    },
    {
      name: "Excessive Fixations Percentage",
      measured: formatPercent(risk.fixationSummary.excessiveFixationsPercentage),
      expected: "Lower is better; average val. N.A.",
    },
  ];

  return (
    <Card withBorder radius="md" p="lg" mt="md">
      <Text size="xs" fw={800} c="dimmed" tt="uppercase" lts={1} mb="md">
        Numeric Summary
      </Text>
      <Table striped highlightOnHover withTableBorder withColumnBorders verticalSpacing="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Measure</Table.Th>
            <Table.Th>Measured</Table.Th>
            <Table.Th>Expected / Reference</Table.Th>
            <Table.Th>Percentile</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Risk Points</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {metricRows.map((row) => (
            <Table.Tr key={row.name}>
              <Table.Td>{row.name}</Table.Td>
              <Table.Td>{row.measured}</Table.Td>
              <Table.Td>{row.expected}</Table.Td>
              <Table.Td>{row.percentile}</Table.Td>
              <Table.Td>
                <Badge color={statusToMantineColor(row.status)} size="sm" radius="sm">
                  {statusToLabel(row.status)}
                </Badge>
              </Table.Td>
              <Table.Td>{row.points}</Table.Td>
            </Table.Tr>
          ))}
          {fixationRows.map((row) => (
            <Table.Tr key={row.name}>
              <Table.Td>{row.name}</Table.Td>
              <Table.Td>{row.measured}</Table.Td>
              <Table.Td>{row.expected}</Table.Td>
              <Table.Td>-</Table.Td>
              <Table.Td>-</Table.Td>
              <Table.Td>-</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Text size="xs" c="dimmed" mt="sm">
        Percentiles are performance percentiles: higher is better. Reading speed uses age-band
        mean/SD; the other metric distributions are derived from the current risk thresholds.
      </Text>
    </Card>
  );
};

const ScoreBreakdown: React.FC<{ risk: ReadingRiskResult }> = ({ risk }) => {
  const items = [
    { label: "Reading Speed", points: risk.metrics.speed.points, max: 2 },
    { label: "Fixation Frequency", points: risk.metrics.fixations.points, max: 2 },
    { label: "Regression Rate", points: risk.metrics.regressions.points, max: 2 },
    { label: "Fixation Duration", points: risk.metrics.duration.points, max: 1 },
  ];
  const bands = [
    { range: "0-4", label: "Reading Ability Within Expected Range", color: "green" },
    { range: "5-6", label: "Monitor Reading Ability", color: "yellow" },
    { range: "7", label: "High Risk of Reading Disturbance", color: "red" },
  ];

  return (
    <Card withBorder radius="md" p="lg" bg="gray.0" h="100%">
      <Text size="xs" fw={800} c="dimmed" tt="uppercase" lts={1} mb="md">
        Score Breakdown (0-7 Points)
      </Text>
      <Stack gap="sm">
        {items.map((item) => (
          <Group key={item.label} justify="space-between" wrap="nowrap">
            <Box>
              <Text size="sm" fw={700} c="dark.7">
                {item.label}
              </Text>
              <Text size="10px" c="dimmed" fw={600}>
                Contribution to risk pattern
              </Text>
            </Box>
            <Badge
              color={item.points > 0 ? (item.points === 2 ? "red" : "orange") : "green.6"}
              size="lg"
              radius="sm"
              style={{ width: rem(65) }}
            >
              +{item.points}
            </Badge>
          </Group>
        ))}
        <Divider my="xs" label="Total Assessment Score" labelPosition="center" />
        <Flex justify="space-between" align="center">
          <Text fw={800} size="md">
            Final Risk Points
          </Text>
          <Text fw={900} size="xl" c={risk.riskColor}>
            {risk.totalScore} / 7
          </Text>
        </Flex>
        <Divider my="xs" label="Classification" labelPosition="center" />
        <Stack gap={6}>
          {bands.map((band) => (
            <Group key={band.range} justify="space-between" wrap="nowrap">
              <Text size="xs" fw={700} c="gray.7">
                {band.range} points
              </Text>
              <Badge color={band.color} radius="sm">
                {band.label}
              </Badge>
            </Group>
          ))}
        </Stack>
      </Stack>
    </Card>
  );
};

export const ReadingAssessmentDashboard: React.FC<Props> = ({ risk }) => {
  return (
    <Box className="ra-dashboard">
      <Grid gutter="xl" mb="xl" align="stretch">
        <Grid.Col span={{ base: 12, md: 8 }} className="ra-col-8">
          <ReadingRiskOverviewHeader risk={risk} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }} className="ra-col-4">
          <ReadingRiskGauge risk={risk} />
        </Grid.Col>
      </Grid>

      <Grid gutter="xl" mb="xl" align="stretch">
        <Grid.Col span={{ base: 12, md: 8 }} className="ra-col-8">
          <ReadingInsightCard risk={risk} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }} className="ra-col-4">
          <ScoreBreakdown risk={risk} />
        </Grid.Col>
      </Grid>

      <Grid gutter="lg" mb="xl" align="stretch">
        <Grid.Col span={{ base: 12, md: 9 }} className="ra-col-9">
          <Flex direction="column" h="100%">
            <Title order={3} c="gray.8" fw={800} mb="sm">
              Primary Risk Factors
            </Title>
            <Grid gutter="lg" align="stretch" style={{ flex: 1 }}>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }} className="ra-col-4">
                <ReadingRiskMetricCard metric={risk.metrics.speed} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }} className="ra-col-4">
                <ReadingRiskMetricCard metric={risk.metrics.fixations} />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 4 }} className="ra-col-4">
                <ReadingRiskMetricCard metric={risk.metrics.duration} />
              </Grid.Col>
            </Grid>
          </Flex>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 3 }} className="ra-col-3">
          <Flex direction="column" h="100%">
            <Title order={3} c="gray.8" fw={800} mb="sm">
              Supporting Indicators
            </Title>
            <Box style={{ flex: 1, display: "flex" }}>
              <ReadingRiskMetricCard metric={risk.metrics.regressions} />
            </Box>
          </Flex>
        </Grid.Col>
      </Grid>
    </Box>
  );
};

export const ReadingAssessmentScoreSummary: React.FC<Props> = ({ risk }) => {
  return (
    <Box className="ra-dashboard">
      <Flex justify="space-between" align="center" px="md" py="sm" wrap="wrap" gap="xs">
        <Group gap="xs">
          <Text fw={800} size="md">
            Total Risk Score:
          </Text>
          <Text fw={900} size="lg" c={risk.riskColor}>
            {risk.totalScore} / 7
          </Text>
        </Group>
        <Text size="xs" c="dimmed">
          Classification: 0-4 expected range, 5-6 monitor reading ability, 7 high risk of reading
          disturbance.
        </Text>
      </Flex>
      <NumericSummaryTable risk={risk} />
    </Box>
  );
};
